// Cartões de crédito: limite, dia de fechamento e de vencimento, compras (à vista ou parceladas) e faturas.
import { Recolhivel } from '../Recolhivel';
import { useState } from 'react';
import { Alert, Pressable, ScrollView, Text, View } from 'react-native';
import type { AlertaItem, Cartao, CompraCartao, Financas } from '../../store/types';
import { AlertasEditor } from '../AlertasEditor';
import { opcoesDias } from '../../lib/alertasData';
import {
  alternarFatura, cartoesDe, excluirCartao, excluirCompra, faturaAtual, faturaDaCompra, faturaPaga, fechaFatura, itensDaFatura, proximasFaturas,
  alertasDaConta, salvarCartao, salvarCompras, ordenarCartoes, duplicarCartao, somaMes, totalFatura, usoDoCartao, valorParcela, vencFatura, type ItemFatura,
} from '../../store/financas';
import { PickerModal } from '../Pickers';
import { Ordenavel } from '../Arrastar';
import { AcoesItem, acoesDoMenu, useAcoes } from '../Acoes';
import { Arquivados, useSelecao } from '../Selecao';
import { useOrganizar } from '../../lib/organizar';
import { paraLixeira } from '../../lib/lixeira';
import { getDoc } from '../../store/store';
import { ActionSheet, Btn, Chip, Field, Sheet, type Action } from '../ui';
import { C, alpha } from '../theme';
import { brYmd, nomeMes, parseYmd, today, uid, ymd } from '../../lib/date';
import { lerValor } from '../../lib/moeda';
import { t } from '../../lib/i18n';
import { CATS, nomeCat } from './cats';
import { useCaixas } from '../../lib/caixas';
import { ROLAGEM_TECLADO } from '../teclado';

const ICONES = ['💳', '🟣', '🟠', '🔵', '⚫', '🟢', '🔴', '🟡', '⚪', '🟤'];
const mesAno = (ym: string) => { const n = nomeMes(Number(ym.slice(5)) - 1); return `${n.charAt(0).toUpperCase()}${n.slice(1)} ${ym.slice(0, 4)}`; };

type FormCartao = { id: string; nome: string; icone: string; limiteTxt: string; fecha: number; vence: number; novo: boolean; alertas?: AlertaItem[] };
export type FormCompra = { id: string; cartao: string; desc: string; valorTxt: string; parcelas: number; data: string; cat: string; novo: boolean };
export const compraVazia = (cartao: string): FormCompra => ({ id: uid(), cartao, desc: '', valorTxt: '', parcelas: 1, data: today(), cat: 'outros', novo: true });

function Barra({ pct, cor }: { pct: number; cor: string }) {
  return (
    <View style={{ height: 6, borderRadius: 3, backgroundColor: C.card2, overflow: 'hidden' }}>
      <View style={{ width: `${Math.max(2, Math.min(100, pct * 100))}%`, height: 6, backgroundColor: pct > 0.9 ? C.danger : pct > 0.75 ? C.warn : cor }} />
    </View>
  );
}

/** Exclui o cartão guardando na lixeira (30 dias) o cartão, as compras e as faturas pagas dele. */
export function apagarCartao(c: Cartao) {
  const d = getDoc('financas');
  paraLixeira('financas', 'financas', c.nome, { cartoes: [c], compras: (d.compras ?? []).filter(x => x.cartao === c.id),
    faturasPagas: (d.faturasPagas ?? []).filter(x => x.startsWith(c.id + ':')) });
  excluirCartao(c.id);
}
/** Exclui uma compra do cartão guardando na lixeira (30 dias). */
export function apagarCompra(cp: CompraCartao) {
  paraLixeira('financas', 'lista', cp.desc, cp, { doc: 'financas', campo: 'compras' });
  excluirCompra(cp.id);
}

/** Lista dos cartões na tela de Finanças, com a fatura do mês e o limite livre. */
export function SecaoCartoes({ d, ym, cor, $, abrir }: { d: Financas; ym: string; cor: string; $: (v: number) => string; abrir: (id: string) => void }) {
  const cartoes = cartoesDe(d);
  const cx = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  const org = useOrganizar('financas-cartao');
  const excluirComConfirmacao = useExcluirCartao();
  const sep = org.separar(cartoes, c => c.id);
  const sel = useSelecao({ itens: sep.ativos, chave: c => c.id, cor, grande: () => true, onArquivar: ids => org.arquivar(ids),
    onExcluir: ids => ids.forEach(id => { const c = cartoes.find(x => x.id === id); if (c) apagarCartao(c); }) });
  if (!cartoes.length) return null;
  return (<>
    <Recolhivel titulo={t('CARTÕES')} visual="titulo" chave="fin:cartoes" resumo={String(sep.ativos.length)} gap={10}>
    {sel.barra}
    <Ordenavel itens={sep.ativos} chave={c => c.id} onOrdem={l => ordenarCartoes([...l, ...sep.arquivados])} selecao={sel.prop} render={c => {
      const u = usoDoCartao(d, c), tot = totalFatura(d, c.id, ym), paga = faturaPaga(d, c.id, ym);
      return (
        <AcoesItem lembrar={{ aba: 'financas', id: c.id, titulo: c.nome }} nome={c.nome} protegido aviso={t('Apaga o cartão e as compras dele.')} onExcluir={() => apagarCartao(c)} onDuplicar={() => duplicarCartao(c)}
          onArquivar={() => org.arquivar(c.id)} fixar={{ on: org.fixado(c.id), alternar: () => org.alternarFixo(c.id) }}
          pacote={() => ({ titulo: t('Fatura {c}', { c: c.nome }), emoji: '💳', data: vencFatura(c, ym), origem: 'financas', texto: tot > 0 ? $(tot) : '' })}>
        <Pressable onPress={() => abrir(c.id)} style={{ backgroundColor: C.card, borderRadius: cx.raio(14), ...cx.pads(12), gap: cx.gap(6), borderWidth: 1, borderColor: C.line }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <Text style={{ fontSize: cx.icone(22) }}>{c.icone}</Text>
            <View style={{ flex: 1 }}>
              <Text style={{ color: C.text, fontWeight: '700' }} numberOfLines={1}>{c.nome}</Text>
              <Text style={{ color: C.sub, fontSize: 12 }}>{tot > 0 ? t('Fatura de {mes}: vence {data}', { mes: nomeMes(Number(ym.slice(5)) - 1), data: brYmd(vencFatura(c, ym)) }) + (paga ? ' · ✓ ' + t('paga') : '')
                : t('Sem fatura em {mes}', { mes: nomeMes(Number(ym.slice(5)) - 1) })}</Text>
            </View>
            <Text style={{ color: paga ? C.sub : C.text, fontWeight: '800', textDecorationLine: paga ? 'line-through' : 'none' }}>{$(tot)}</Text>
          </View>
          {c.limite > 0 && (<>
            <Barra pct={u.pct} cor={cor} />
            <Text style={{ color: C.mute, fontSize: 11 }}>{t('Limite livre: {v} de {l}', { v: $(u.livre), l: $(c.limite) })}</Text>
          </>)}
        </Pressable>
        </AcoesItem>
      );
    }} />
    </Recolhivel>
    <Arquivados itens={sep.arquivados} chave={c => c.id} cor={cor} qualificador={t('Cartões')} render={c => <Text style={{ color: C.text }}>{c.icone} {c.nome}</Text>}
      onRestaurar={c => org.restaurar(c.id)} onExcluir={c => excluirComConfirmacao(c)} />
  </>);
}

/** Excluir um cartão fora da lista com arraste: pergunta, pede a senha ou a biometria e vai para a lixeira. */
function useExcluirCartao() {
  const { confirmar } = useAcoes();
  return (c: Cartao, depois?: () => void) => Alert.alert(t('Excluir o cartão "{c}"?', { c: c.nome }), t('As compras dele também saem das Finanças.') + '\n\n' + t('Fica 30 dias na lixeira.'), [
    { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => { depois?.(); setTimeout(async () => {
      if (await confirmar(t('Confirme para excluir'))) apagarCartao(c); }, depois ? 400 : 0); } }]);
}

/** Cadastro ou edição de um cartão. */
export function CartaoSheet({ form, setForm, cor }: { form: FormCartao | null; setForm: (f: FormCartao | null) => void; cor: string }) {
  function salvar() {
    if (!form) return;
    if (!form.nome.trim()) return Alert.alert(t('Dê um nome ao cartão.'));
    salvarCartao({ id: form.id, nome: form.nome.trim(), icone: form.icone, limite: Math.max(0, lerValor(form.limiteTxt) || 0), fecha: form.fecha, vence: form.vence, alertas: form.alertas });
    setForm(null);
  }
  const Dias = ({ v, on }: { v: number; on: (n: number) => void }) => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6 }}>
      {Array.from({ length: 31 }, (_, i) => i + 1).map(n => <Chip key={n} small cor={cor} on={v === n} label={String(n)} onPress={() => on(n)} />)}
    </ScrollView>
  );
  return (
    <Sheet visible={!!form} onClose={() => setForm(null)} title={form?.novo ? t('Novo cartão') : t('Editar cartão')}
      right={<Pressable onPress={salvar}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable>}>
      {form && (
        <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }}>
          <Field value={form.nome} onChangeText={v => setForm({ ...form, nome: v })} placeholder={t('Nome (ex.: Nubank, Itaú Visa)')} />
          <Field value={form.limiteTxt} onChangeText={v => setForm({ ...form, limiteTxt: v })} keyboardType="decimal-pad" placeholder={t('Limite total (opcional)')} />
          <Text style={{ color: C.sub }}>{t('Dia em que a fatura fecha')}</Text>
          <Dias v={form.fecha} on={n => setForm({ ...form, fecha: n })} />
          <Text style={{ color: C.sub }}>{t('Dia do vencimento')}</Text>
          <Dias v={form.vence} on={n => setForm({ ...form, vence: n })} />
          {/* texto 10 (A4): o mesmo editor de alertas da Nova tarefa, para cada fatura (dias antes, hora e "Como avisar") */}
          <Text style={{ color: C.sub }}>🔔 {t('Alertas de cada fatura')}</Text>
          <AlertasEditor itens={alertasDaConta({ tipo: 'pagar', alertas: form.alertas })} temHorario={false} cor={cor} aba="financas"
            dias={opcoesDias([getDoc('ajustes').contasAntes ?? 3])} onChange={l => setForm({ ...form, alertas: l })} />
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {ICONES.map(i => <Pressable key={i} onPress={() => setForm({ ...form, icone: i })} style={{ width: 40, height: 40, borderRadius: 10, alignItems: 'center', justifyContent: 'center',
              backgroundColor: form.icone === i ? alpha(cor, 0.3) : C.card2 }}><Text style={{ fontSize: 20 }}>{i}</Text></Pressable>)}
          </View>
          <Text style={{ color: C.mute, fontSize: 11 }}>{t('Os dias estão no app ou na fatura do banco. Compras feitas depois do fechamento caem na fatura seguinte.')}</Text>
          <Btn cor={cor} label={t('Salvar')} onPress={salvar} />
        </ScrollView>
      )}
    </Sheet>
  );
}
export const cartaoVazio = (): FormCartao => ({ id: uid(), nome: '', icone: '💳', limiteTxt: '', fecha: 25, vence: 5, novo: true });
export const formDoCartao = (c: Cartao): FormCartao => ({ ...c, limiteTxt: c.limite ? String(c.limite).replace('.', ',') : '', novo: false });

/** Cadastro de uma compra no cartão (à vista ou parcelada). */
export function CompraSheet({ d, form, setForm, cor, $, aoSalvar }:
  { d: Financas; form: FormCompra | null; setForm: (f: FormCompra | null) => void; cor: string; $: (v: number) => string; aoSalvar?: (cat: string, ym: string) => void }) {
  const [picker, setPicker] = useState(false);
  const cartoes = cartoesDe(d);
  const cartao = cartoes.find(c => c.id === form?.cartao);
  const v = form ? lerValor(form.valorTxt) : 0;
  function salvar() {
    if (!form || !cartao) return;
    if (!form.desc.trim() || !(v > 0)) return Alert.alert(t('Informe a descrição e o valor.'));
    const c: CompraCartao = { id: form.id, cartao: cartao.id, data: form.data, desc: form.desc.trim(), valor: Math.round(v * 100) / 100, parcelas: form.parcelas,
      cat: form.cat, fatura1: faturaDaCompra(cartao, form.data) };
    salvarCompras([c], form.novo ? faturaAtual(cartao) : undefined);
    setForm(null);
    aoSalvar?.(c.cat, c.fatura1);
  }
  const f1 = cartao && form ? faturaDaCompra(cartao, form.data) : '';
  return (
    <Sheet visible={!!form} onClose={() => setForm(null)} title={form?.novo ? t('Compra no cartão') : t('Editar compra')}
      right={<Pressable onPress={salvar}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable>}>
      {form && (
        <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }}>
          {cartoes.length > 1 && <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {cartoes.map(c => <Chip key={c.id} cor={cor} on={form.cartao === c.id} label={`${c.icone} ${c.nome}`} onPress={() => setForm({ ...form, cartao: c.id })} />)}
          </View>}
          <Field value={form.desc} onChangeText={x => setForm({ ...form, desc: x })} placeholder={t('O que comprou')} />
          <Field value={form.valorTxt} onChangeText={x => setForm({ ...form, valorTxt: x })} keyboardType="decimal-pad" placeholder={t('Valor total da compra')} />
          <View style={{ flexDirection: 'row' }}><Chip cor={cor} on={false} label={`📅 ${brYmd(form.data)}`} onPress={() => setPicker(true)} /></View>
          <Text style={{ color: C.sub }}>{t('Parcelas')}</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6 }}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 15, 18, 21, 24].map(n => <Chip key={n} small cor={cor} on={form.parcelas === n} label={n === 1 ? t('À vista') : `${n}x`} onPress={() => setForm({ ...form, parcelas: n })} />)}
          </ScrollView>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {CATS.filter(c => c[0] !== 'invest').map(([k, l]) => <Chip key={k} small cor={cor} on={form.cat === k} label={t(l)} onPress={() => setForm({ ...form, cat: k })} />)}
          </View>
          {!!cartao && v > 0 && <View style={{ backgroundColor: alpha(cor, 0.1), borderRadius: 12, padding: 10, gap: 2 }}>
            <Text style={{ color: C.text, fontSize: 13 }}>{form.parcelas > 1 ? t('{n}x de {v}', { n: form.parcelas, v: $(valorParcela({ valor: v, parcelas: form.parcelas } as CompraCartao, 1)) }) : $(v)}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{t('Primeira parcela na fatura de {mes}, que vence {data}.', { mes: mesAno(f1), data: brYmd(vencFatura(cartao, f1)) })}</Text>
          </View>}
          <Btn cor={cor} label={t('Salvar')} onPress={salvar} />
        </ScrollView>
      )}
      {picker && form && <PickerModal mode="date" cor={cor} value={parseYmd(form.data)} onClose={() => setPicker(false)} onPick={x => setForm({ ...form, data: ymd(x) })} />}
    </Sheet>
  );
}

/** Um cartão aberto: faturas mês a mês, compras de cada fatura, limite e o que já está comprometido. */
export function DetalheCartao({ d, id, onClose, cor, $, importarFatura, aoSalvarCompra }:
  { d: Financas; id: string | null; onClose: () => void; cor: string; $: (v: number) => string; importarFatura: (cartao: string) => void; aoSalvarCompra?: (cat: string, ym: string) => void }) {
  const c = cartoesDe(d).find(x => x.id === id) ?? null;
  const cx = useCaixas();
  const [ym, setYm] = useState<string | null>(null);
  const [form, setForm] = useState<FormCartao | null>(null);
  const [compra, setCompra] = useState<FormCompra | null>(null);
  const [menu, setMenu] = useState<{ t: string; a: Action[] } | null>(null);
  const org = useOrganizar('financas-cartao');
  const { enviar } = useAcoes();
  const excluirCartaoConf = useExcluirCartao();
  if (!c) return <Sheet visible={false} onClose={onClose} title=""><View /></Sheet>;
  const atual = ym ?? faturaAtual(c);
  const itens = itensDaFatura(d, c.id, atual), tot = totalFatura(d, c.id, atual), paga = faturaPaga(d, c.id, atual);
  const u = usoDoCartao(d, c);
  const fechar = () => { setYm(null); onClose(); };
  const opcoes = (i: ItemFatura) => setMenu({ t: i.compra.desc, a: [
    { label: t('Editar'), onPress: () => setCompra({ id: i.compra.id, cartao: i.compra.cartao, desc: i.compra.desc, valorTxt: String(i.compra.valor).replace('.', ','), parcelas: i.compra.parcelas, data: i.compra.data, cat: i.compra.cat, novo: false }) },
    ...CATS.filter(x => x[0] !== 'invest').map(([k, l]) => ({ label: (i.compra.cat === k ? '✓ ' : '') + t(l), onPress: () => salvarCompras([{ ...i.compra, cat: k }]) })),
    { label: t('Excluir'), destructive: true, onPress: () => Alert.alert(t('Excluir a compra?'), [i.compra.parcelas > 1 ? t('Apaga todas as {n} parcelas.', { n: i.compra.parcelas }) : i.compra.desc, t('Fica 30 dias na lixeira.')].join('\n\n'), [
      { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => apagarCompra(i.compra) }]) },
  ] });
  return (
    <Sheet visible={!!id} onClose={fechar} title={`${c.icone} ${c.nome}`}
      right={<Pressable onPress={() => setMenu({ t: c.nome, a: [
        { label: t('Editar cartão'), onPress: () => setForm(formDoCartao(c)) },
        ...acoesDoMenu(enviar, { pacote: () => ({ titulo: t('Fatura {c}', { c: c.nome }), emoji: '💳', data: vencFatura(c, atual), origem: 'financas', texto: tot > 0 ? $(tot) : '' }),
          fixar: { on: org.fixado(c.id), alternar: () => org.alternarFixo(c.id) } }),
        { label: '📦 ' + t('Arquivar'), onPress: () => { org.arquivar(c.id); fechar(); } },
        { label: t('Excluir cartão'), destructive: true, onPress: () => excluirCartaoConf(c, fechar) },
      ] })} hitSlop={10} accessibilityLabel={t('Mais opções')}><Text style={{ color: cor, fontWeight: '800', fontSize: 20 }}>⋮</Text></Pressable>}>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 80 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Pressable onPress={() => setYm(somaMes(atual, -1))} hitSlop={12} style={{ padding: 6 }}><Text style={{ color: C.sub, fontSize: 24 }}>‹</Text></Pressable>
          <Text style={{ flex: 1, textAlign: 'center', color: C.text, fontSize: 16, fontWeight: '800' }}>{t('Fatura de {mes}', { mes: mesAno(atual) })}</Text>
          <Pressable onPress={() => setYm(somaMes(atual, 1))} hitSlop={12} style={{ padding: 6 }}><Text style={{ color: C.sub, fontSize: 24 }}>›</Text></Pressable>
        </View>
        <View style={{ backgroundColor: C.card, borderRadius: 16, padding: 14, gap: 6 }}>
          <Text style={{ color: C.text, fontSize: 24, fontWeight: '800' }}>{$(tot)}</Text>
          <Text style={{ color: C.sub, fontSize: 13 }}>{t('Fecha {f} · vence {v}', { f: brYmd(fechaFatura(c, atual)), v: brYmd(vencFatura(c, atual)) })}</Text>
          {tot > 0 && <Pressable onPress={() => alternarFatura(c.id, atual)} style={{ alignSelf: 'flex-start', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, backgroundColor: paga ? alpha(C.ok, 0.18) : alpha(cor, 0.18) }}>
            <Text style={{ color: paga ? C.ok : cor, fontWeight: '700', fontSize: 13 }}>{paga ? '✓ ' + t('Fatura paga (toque para desfazer)') : t('Marcar fatura como paga')}</Text>
          </Pressable>}
          {c.limite > 0 && (<>
            <Barra pct={u.pct} cor={cor} />
            <Text style={{ color: C.mute, fontSize: 12 }}>{t('Usado: {u} · livre: {v} · limite {l}', { u: $(u.usado), v: $(u.livre), l: $(c.limite) })}</Text>
          </>)}
        </View>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <View style={{ flex: 1 }}><Btn cor={cor} label={'+ ' + t('Compra')} onPress={() => setCompra(compraVazia(c.id))} /></View>
          <View style={{ flex: 1 }}><Btn outline cor={cor} label={'📷 ' + t('Ler a fatura')} onPress={() => { fechar(); setTimeout(() => importarFatura(c.id), 450); }} /></View>
        </View>
        {!itens.length && <Text style={{ color: C.sub }}>{t('Nenhuma compra nesta fatura.')}</Text>}
        {itens.map(i => (
          <Pressable key={i.compra.id + i.k} onPress={() => opcoes(i)} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: cx.pad(8), paddingHorizontal: cx.padH(12), borderRadius: cx.raio(12), backgroundColor: C.card }}>
            <Text style={{ color: C.mute, fontSize: 12, width: 42 }}>{brYmd(i.compra.data).slice(0, 5)}</Text>
            <View style={{ flex: 1 }}>
              <Text style={{ color: C.text }} numberOfLines={1}>{i.compra.desc}</Text>
              <Text style={{ color: C.mute, fontSize: 11 }}>{nomeCat(i.compra.cat)}{i.compra.parcelas > 1 ? ' · ' + t('parcela {a} de {n}', { a: i.k + 1, n: i.compra.parcelas }) : ''}</Text>
            </View>
            <Text style={{ color: i.valor < 0 ? C.ok : C.text, fontWeight: '700' }}>{$(i.valor)}</Text>
          </Pressable>
        ))}
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 8 }}>{t('PRÓXIMAS FATURAS (JÁ COMPROMETIDO)')}</Text>
        {proximasFaturas(d, c, 6).map(f => (
          <Pressable key={f.ym} onPress={() => setYm(f.ym)} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 }}>
            <Text style={{ color: f.ym === atual ? cor : C.text }}>{mesAno(f.ym)}</Text>
            <Text style={{ color: f.paga ? C.ok : C.sub }}>{f.paga ? '✓ ' : ''}{$(f.total)}</Text>
          </Pressable>
        ))}
      </ScrollView>
      <CartaoSheet form={form} setForm={setForm} cor={cor} />
      <CompraSheet d={d} form={compra} setForm={setCompra} cor={cor} $={$} aoSalvar={aoSalvarCompra} />
      <ActionSheet title={menu?.t ?? ''} actions={menu?.a ?? null} onClose={() => setMenu(null)} />
    </Sheet>
  );
}
